import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';

import { TourGuide } from '@/tour-guide';
import { buildWorkspacesTour, workspacesIntroDescription } from '@/tour-guide/tours';
import { TourRunner } from './WorkspaceTourRunner';
import { createApiProgressStore } from '@/tour-guide/engine/progressStore';
import { useAuth } from '@/components/AuthProvider';
import { useIsMobile } from '@/hooks/use-mobile';
import { canCreateWorkspace } from '@/lib/workspaces/manageWorkspaces';
import { markTourSeenOnServer } from '@/lib/api/services/tourSeen';
import { hasBlockingDialog, subscribeBlockingDialogs } from '@/lib/blockingDialogs';
import { CreateWorkspaceDialog } from './CreateWorkspaceDialog';
import { useSwitchWorkspace } from '@/hooks/useSwitchWorkspace';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import {
  WORKSPACES_TOUR_ID,
  shouldShowWorkspacesCard,
  isTourForced,
  hasSeenTour,
  hasSeenTourLocally,
  markTourSeenLocally,
  syncSeenToursFromPreferences,
  withSeenTour,
  effectiveReleasedAt,
  countConnectedWorkspaces,
} from '@/lib/workspaceTour';
import { Button } from '@/components/ui/button';

const WORKSPACES_INTRO_IMAGE = '/workspaces-intro.png';

function CardArt() {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="relative h-[168px] w-full overflow-hidden border-b border-[#EAECF0] bg-gradient-to-b from-[#DBEAFE] via-brand-soft to-white">
      <img
        src={WORKSPACES_INTRO_IMAGE}
        alt=""
        aria-hidden
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(false)}
        className={
          loaded
            ? 'absolute left-1/2 -top-[3px] -ml-1 w-[86%] -translate-x-1/2 drop-shadow-sm'
            : 'hidden'
        }
      />
    </div>
  );
}

export function WorkspaceTourHost() {
  const { user, updateUser } = useAuth();
  const isMobile = useIsMobile();
  const switchWorkspace = useSwitchWorkspace();
  const { workspaces } = useWorkspaceContext();

  const [screenBusy, setScreenBusy] = useState(() => hasBlockingDialog());
  useEffect(() => subscribeBlockingDialogs(() => setScreenBusy(hasBlockingDialog())), []);

  const [dismissed, setDismissed] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [tourRequested, setTourRequested] = useState(false);

  const userId = user?.id ?? null;
  const preferences = (user as { preferences?: unknown } | null)?.preferences;
  const forced = isTourForced();
  const isAdmin = canCreateWorkspace(user as never);

  const connectedNumbers = useMemo(() => countConnectedWorkspaces(workspaces), [workspaces]);

  const [seenLocally, setSeenLocally] = useState(false);
  useEffect(() => {
    if (!userId) {
      setSeenLocally(false);
      return;
    }
    syncSeenToursFromPreferences(userId, preferences);
    setSeenLocally(hasSeenTourLocally(userId, WORKSPACES_TOUR_ID));
  }, [userId, preferences]);

  const userRef = useRef(user);
  userRef.current = user;
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;
  const updateUserRef = useRef(updateUser);
  updateUserRef.current = updateUser;
  const writtenRef = useRef(false);

  const markSeen = useCallback(() => {
    if (isTourForced()) return;
    if (writtenRef.current) return;
    writtenRef.current = true;

    markTourSeenLocally(userIdRef.current, WORKSPACES_TOUR_ID);
    setSeenLocally(true);

    const current = userRef.current;
    if (current) {
      const next = withSeenTour(preferencesRef.current, WORKSPACES_TOUR_ID);
      if (next !== preferencesRef.current) {
        updateUserRef.current({ ...(current as object), preferences: next } as never);
      }
    }

    void markTourSeenOnServer(WORKSPACES_TOUR_ID);
  }, []);

  const eligible = shouldShowWorkspacesCard({
    user,
    preferences,
    isScreenBusy: screenBusy,
    releasedAt: effectiveReleasedAt(),
    forced,
    seenLocally,
  });

  const showCard = eligible && !dismissed;

  const close = useCallback(() => {
    setDismissed(true);
    markSeen();
  }, [markSeen]);

  const onGetStarted = useCallback(() => {
    close();
    setCreateOpen(true);
  }, [close]);

  const onGuidedTour = useCallback(() => {
    close();
    setTourRequested(true);
  }, [close]);

  const onTourFinished = useCallback(() => setTourRequested(false), []);

  const tours = useMemo(
    () => [buildWorkspacesTour({ connectedNumbers })],
    [connectedNumbers],
  );

  const progressStore = useMemo(
    () =>
      createApiProgressStore({
        get: () =>
          hasSeenTour(preferencesRef.current, WORKSPACES_TOUR_ID) ||
          hasSeenTourLocally(userIdRef.current, WORKSPACES_TOUR_ID)
            ? { [WORKSPACES_TOUR_ID]: true }
            : {},
        put: (progress) => {
          if (progress[WORKSPACES_TOUR_ID]) markSeen();
        },
      }),
    [markSeen],
  );

  if (!showCard && !tourRequested && !createOpen) return null;

  const offerTour = !isMobile;

  return (
    <TourGuide
      enabled
      tours={tours}
      progressStore={progressStore}
      withLauncher={false}
      withTracker={false}
    >
      <TourRunner requested={tourRequested} onFinished={onTourFinished} />

      <CreateWorkspaceDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => switchWorkspace(id)}
      />

      {showCard && (
        <div
          role="status"
          aria-label="Introducing workspaces"
          className="
            fixed z-[60] overflow-hidden rounded-2xl border border-[#EAECF0] bg-white
            shadow-[0_12px_32px_-8px_rgba(16,24,40,0.18)]
            inset-x-4 bottom-4
            sm:inset-x-auto sm:bottom-3 sm:left-3 sm:w-[400px]
          "
          style={{ fontFamily: 'var(--wn-font)' }}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Dismiss"
            className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-white/80 text-[#667085] backdrop-blur transition hover:bg-slate-50 hover:text-[#101828]"
          >
            <X className="h-4 w-4" />
          </button>

          <CardArt />

          <div className="px-5 pb-4 pt-5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-brand">New</p>
            <h2 className="mt-1 text-[19px] font-bold leading-tight text-[#101828]">
              Introducing workspaces
            </h2>
            <p className="mt-2 text-[13.5px] leading-relaxed text-[#475467]">
              {workspacesIntroDescription(connectedNumbers)}
            </p>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-[#EAECF0] px-5 py-3">
            {isAdmin ? (
              offerTour ? (
                <button
                  type="button"
                  onClick={onGetStarted}
                  className="text-[13px] font-semibold text-[#475467] transition hover:text-[#101828]"
                >
                  Get started
                </button>
              ) : (
                <Button size="sm" onClick={onGetStarted}>
                  Get started
                </Button>
              )
            ) : offerTour ? (
              <Button size="sm" onClick={onGuidedTour}>
                Show me
              </Button>
            ) : (
              <Button size="sm" onClick={close}>
                Got it
              </Button>
            )}

            {isAdmin && offerTour ? (
              <Button size="sm" onClick={onGuidedTour}>
                Get guided tour
              </Button>
            ) : (
              <span />
            )}
          </div>
        </div>
      )}
    </TourGuide>
  );
}

export default WorkspaceTourHost;
